import React, { createRef } from 'react';
import classNames from 'classnames';
import './toolTipTiny.module.scss';
import { useSpring, animated } from 'react-spring';
import { observer } from 'mobx-react';
import { viewportState } from '../../../../commonv2/states/globalStates';
import useComponentSize from '@rehooks/component-size';

const VERTICAL_ANIMATION_OFFSET = 10;

type ContainerProps = {
  id?: string;
  position?: ToolTipPosition;
  horizontalOffset?: number;
  nowrap?: boolean;
  target: React.RefObject<HTMLDivElement>;
  toolTipRef: React.RefObject<HTMLDivElement>;
};

const ToolTipContainer: React.FC<ContainerProps> = ({
  children,
  id = undefined,
  position = 'top',
  horizontalOffset = 0,
  nowrap = false,
  target,
  toolTipRef,
}) => {
  const targetSize = useComponentSize(target);
  const { width, height } = targetSize;
  const toolTipSize = useComponentSize(toolTipRef);
  const toolTipWidth = toolTipSize.width;
  const toolTipHeight = toolTipSize.height;
  const fromMarginTop = position == 'top' ? -toolTipHeight : height;
  const toMarginTop =
    position == 'top' ? fromMarginTop - VERTICAL_ANIMATION_OFFSET : fromMarginTop + VERTICAL_ANIMATION_OFFSET;
  const marginLeft = viewportState.isMobile ? 0 : -(toolTipWidth / 2 - width / 2) + horizontalOffset;
  const styles = useSpring({
    from: { opacity: 0, marginTop: fromMarginTop },
    to: { opacity: 1, marginTop: toMarginTop },
    reset: true,
  });

  return (
    <animated.div
      id={id}
      style={{ ...styles, marginLeft }}
      styleName={classNames('tooltip', position, { mobile: viewportState.isMobile, nowrap })}
      ref={toolTipRef}
    >
      {children}
    </animated.div>
  );
};

export type ToolTipPosition = 'top' | 'bottom';

type Props = {
  id?: string;
  description: string;
  position?: ToolTipPosition;
  horizontalOffset?: number;
  nowrap?: boolean;
  opened: boolean;
};

export const ToolTipTiny: React.FC<Props> = observer(
  ({ id, description, position, horizontalOffset, nowrap, opened, children }) => {
    const containerRef = createRef<HTMLDivElement>();
    const toolTipRef = createRef<HTMLDivElement>();

    const tooltipPosition = position ? position : viewportState.isMobile ? 'bottom' : 'top';
    return (
      <div ref={containerRef}>
        {opened && (
          <ToolTipContainer
            id={id}
            position={tooltipPosition}
            horizontalOffset={horizontalOffset}
            nowrap={nowrap}
            target={containerRef}
            toolTipRef={toolTipRef}
          >
            <div styleName="tooltip-content">{description}</div>
          </ToolTipContainer>
        )}
        {children}
      </div>
    );
  }
);
